import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import {
  catalogPrefixFor,
  DEFAULT_ITEM_TYPE,
  itemTypeForCatalogPrefix,
  type ItemTypeSlug,
} from "@/lib/item-types";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Return the public inventory code for a type-scoped catalog number.
 */
export function formatCatalogCode(
  itemType: string | null | undefined,
  catalogNumber: number
): string {
  const prefix = catalogPrefixFor(itemType);
  const paddedNumber = catalogNumber.toString().padStart(3, "0");
  return prefix ? `${prefix}${paddedNumber}` : `${itemType || "item"} ${paddedNumber}`;
}

/** Parse a number or public code, optionally requiring the selected item type. */
export function parseCatalogNumber(
  input: string | number,
  itemType?: string | null
): number | null {
  if (typeof input === "number") {
    return Number.isSafeInteger(input) && input >= 0 ? input : null;
  }

  const trimmed = input.trim();
  const match = trimmed.match(/^(?:([A-Z]+)\s*)?(\d+)$/i);
  if (!match) return null;

  if (match[1]) {
    const parsedType = itemTypeForCatalogPrefix(match[1]);
    if (!parsedType || (itemType && parsedType !== itemType)) return null;
  }

  const catalogNumber = Number.parseInt(match[2], 10);
  return Number.isSafeInteger(catalogNumber) && catalogNumber >= 0
    ? catalogNumber
    : null;
}

/** Parse a route/catalog identifier into the composite catalog identity. */
export function parseCatalogIdentity(
  input: string,
  defaultItemType: ItemTypeSlug = DEFAULT_ITEM_TYPE
): { itemType: ItemTypeSlug; catalogNumber: number } | null {
  const match = input.trim().match(/^(?:([A-Z]+)\s*)?(\d+)$/i);
  if (!match) return null;

  const itemType = match[1]
    ? itemTypeForCatalogPrefix(match[1])
    : defaultItemType;
  if (!itemType) return null;

  const catalogNumber = Number.parseInt(match[2], 10);
  if (!Number.isSafeInteger(catalogNumber) || catalogNumber < 0) return null;

  return { itemType, catalogNumber };
}

/**
 * Parse a catalog search while preserving leading zeroes for partial matching.
 * A prefixed search is restricted to that type; a numeric search spans types.
 */
export function parseCatalogCodeSearch(input: string): {
  digits: string;
  itemType: ItemTypeSlug | null;
} | null {
  const match = input.trim().match(/^(?:([A-Z]+)\s*)?(\d+)$/i);
  if (!match) return null;

  const itemType = match[1] ? itemTypeForCatalogPrefix(match[1]) : null;
  if (match[1] && !itemType) return null;

  return { digits: match[2], itemType };
}

/**
 * Format a price value to display with euro symbol (€)
 * Strips all non-numeric characters and formats consistently as "X €"
 * @param price - The price string (e.g., "350", "350€", "€350", "$350")
 * @returns Formatted price with € symbol, or null if no price
 */
export function formatPrice(price: string | null | undefined): string | null {
  if (!price || price.trim() === "") {
    return null;
  }

  // Extract numeric value (digits, decimal points, commas for thousands)
  const numericPart = price.replace(/[^0-9.,]/g, "").trim();

  if (!numericPart) {
    return null;
  }

  return `${numericPart} €`;
}
